import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IonInput } from '@ionic/angular/standalone';
import { UpsDrawer } from '@ux/components/dist/types/components/drawer/ups-drawer';
import {
  catchError,
  filter,
  finalize,
  map,
  of,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AuthenticationService } from 'src/app/core/auth/authentication.service';
import { SpecialCharacters, UserRoles } from 'src/app/core/enums';
import { Screens } from 'src/app/core/enums/role-permissions.enum';
import {
  CurrentLocation,
  GetLocationProfile,
  LocationGroup,
} from 'src/app/core/models/admin/location-admin.model';
import { GetUser, UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { PackageStatusDetails } from 'src/app/core/models/package-status-details.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { ErrorhandleService } from 'src/app/core/services/error-handling/error-handle/errorhandle.service';
import { PackageStatusService } from 'src/app/core/services/package-status/package-status.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ScreenPermissionsUtils } from 'src/app/core/utils/permissions/screen-permissions/screen-permissions.utils';
import { UserProfilePermissionUtils } from 'src/app/core/utils/permissions/user-profile-permissions/user-profile-permissions.utils';
import { LinkedSite } from '../admin/location-group/location-group.constants';
import { BANNER_TYPE } from '../banner/banner.constants';
import { PackageStatusDetailsComponent } from '../shared/modal/package-status-details/package-status-details.component';
import { ProfileModalComponent } from '../shared/modal/profile-modal/profile-modal.component';
import { FORM_STATUS } from '../shared/modal/profile-modal/profile-modal.constants';
import { IMAGE_SRC } from '../shared/shared-constants';
import { DISPLAY_CONSTANTS } from './top-nav.constants';

@Component({
  selector: 'app-top-nav',
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.scss'],
  standalone: false,
})
export class TopNavComponent implements OnInit, OnDestroy {
  updateUser = output();

  commonUtils = inject(CommonUtils);
  permissions = inject(ScreenPermissionsUtils);
  authService = inject(AuthenticationService);
  userService = inject(UserService);
  locationService = inject(LocationService);
  userProfileUtils = inject(UserProfilePermissionUtils);
  route = inject(Router);
  errorHandler = inject(ErrorhandleService);

  private readonly packageStatusService = inject(PackageStatusService);
  private dialog = inject(MatDialog);
  private destroy = inject(DestroyRef);

  userDisplayName = signal(DISPLAY_CONSTANTS.LOADING_TEXT);

  readonly locationSearchRef = viewChild<IonInput>('locationSearch');
  readonly groupSearchRef = viewChild<IonInput>('locationGroupSearch');
  @ViewChild('drawer') drawer?: ElementRef<UpsDrawer>;

  searchQuery = '';
  groupSearch: string = '';
  siteSearch: string = '';
  currentLocation!: CurrentLocation | undefined;
  currentUser!: UserAdmin | undefined;
  userRole!: UserRoles;
  sciIndicator!: number;
  locationGroups!: LocationGroup[];
  linkedSites!: LinkedSite[];

  readonly IMAGES = IMAGE_SRC;
  readonly DISPLAY = DISPLAY_CONSTANTS;
  readonly SCREENS = Screens;
  localDateTime = signal('');
  private dateTimeIntervalId: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.updateLocalDateTime();
    this.dateTimeIntervalId = setInterval(() => {
      this.updateLocalDateTime();
    }, 1000);

    // linked sites and location depend on user state
    // these props need to be updated on user change
    this.userService
      .getCurrentUser$()
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroy),
        catchError(() => {
          return of();
        }),
        filter((val) => !!val),
        switchMap((emitUser) => {
          this.setUserInfo(emitUser);
          if (
            this.userProfileUtils.canEditUserLocationGroup(emitUser.roleName)
          ) {
            return this.getLocationGroups();
          } else {
            return of();
          }
        }),
      )
      .subscribe();

    this.locationService
      .getCurrentLocation$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => !!value),
      )
      .subscribe((newLocation) => {
        if (
          newLocation?.location?.locationDetails?.code &&
          this.currentLocation?.location?.locationDetails?.code
        ) {
          if (
            newLocation.groupCode != this.currentLocation.groupCode ||
            newLocation.location.locationDetails?.code !=
              this.currentLocation.location.locationDetails?.code
          ) {
            this.updateCurrentUser(newLocation);
          }
        }
      });
  }

  toggleLoading(flag: boolean): void {
    this.commonUtils.updateLoadingModal(flag);
    this.commonUtils.lockUpdateLoadingModal(flag);
  }

  toggleDrawer(): void {
    if (this.authService.allowDrawerToggle()) {
      this.authService.isDrawerOpen$
        .asObservable()
        .pipe(
          take(1),
          tap((val) => {
            this.authService.isDrawerOpen$.next(!val);
          }),
        )
        .subscribe();
    }
  }

  
  ngOnDestroy(): void {
    if (this.dateTimeIntervalId !== null) {
      clearInterval(this.dateTimeIntervalId);
      this.dateTimeIntervalId = null;
    }
  }

  private updateLocalDateTime(): void {
    const now = new Date();
    const time = now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const date = now.toLocaleDateString([], {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    this.localDateTime.set(`${time} | ${date}`);
  }

  showSiteControls(code: string): boolean {
    // disabling this until we clarify what is expected
    const validCode = (code && code.length > 0) || false;
    const permission = this.currentUser?.roleName === UserRoles.ADMIN || false; // update this once details for min role are clarified by Farrakh / Almando
    return validCode && permission;
  }

  paste() {
    this.searchQuery.trim();
    this.openPackageTrackingPopup();
  }

  pressKey($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      // need to update event handling here to get text from the search field
      if (this.searchQuery.length === 0) {
        return;
      }
      this.openPackageTrackingPopup();
    } else {
      this.commonUtils.preventSpecialCharacters(
        $event,
        SpecialCharacters.ALPHABET_NUMBER_NO_SPACE,
      );
    }
  }

  openPackageTrackingPopup(): void {
    this.packageStatusService
      .getPackageStatus(this.searchQuery)
      .pipe(take(1))
      .subscribe((packageStatusResponse) => {
        const packageStatus = packageStatusResponse?.response || null;
        if (packageStatus) {
          const packageStatusDetails: PackageStatusDetails = {
            trackingNumber: packageStatus.tracknum,
            reason: packageStatus.smartValues?.reason || '',
            equipmentNumber: packageStatus.uld,
            scanTime: packageStatus.scanTime,
            destination: packageStatus.smartValues?.destCountry || '',
            sortCode:
              packageStatus.smartValues?.code ||
              packageStatus.smartValues?.intlsortCode ||
              '',
            equipmentType: packageStatus.uldType,
            movement: packageStatus.movement,
            userId: packageStatus.userName,
            loadChart: packageStatus.loadChart,
            status: packageStatus.status,
            sortDate: packageStatus.smartValues?.sortDate || '',
            loadKey: packageStatus.loadKey,
            flex: packageStatus.flex,
            childLoad: packageStatus.smartValues?.childLoadChart || '',
          };
          this.openPackageStatusPopup(
            packageStatusDetails,
            !!packageStatus?.smartValues || false,
          );
        }
      });
  }

  openPackageStatusPopup(
    packageStatusDetails: PackageStatusDetails,
    isSmartValue: boolean,
  ) {
    const dialogRef = this.dialog.open(PackageStatusDetailsComponent, {
      width: '50rem',
      disableClose: true,
      data: {
        trackingNumber: this.searchQuery,
        packageStatusDetails,
        isSmartValue,
      },
    });
    dialogRef?.afterClosed().subscribe(() => {
      this.searchQuery = '';
    });
  }

  gotoProfile(): void {
    const dialogRef = this.dialog.open(ProfileModalComponent, {
      width: '52rem',
      disableClose: true,
      data: {
        data: { ...this.currentUser } as UserAdmin,
        formStatus: FORM_STATUS.VIEW_EDIT,
        showButton: true,
        isOwnProfile: true,
      },
    });

    dialogRef.afterClosed().subscribe((status) => {
      if (status?.message === 'Update Successful') {
        this.userService
          .getUser()
          .pipe(
            take(1),
            map((getUser) => {
              if (getUser.response.user) {
                this.userService.setCurrentUser$(getUser.response.user);
              }
            }),
            catchError((error: HttpErrorResponse) => {
              if (
                error.status === 401 &&
                (error.error?.message as string)
                  .toLowerCase()
                  .startsWith('invalid user')
              ) {
                this.route.navigate(['login'], {
                  queryParams: {
                    userId: (error.error?.message as string).slice(13),
                  },
                });
                return of();
              }
              return this.errorHandler.handleError<GetUser>(
                error,
                error.status,
                'User Service: Get User',
                BANNER_TYPE.GET_CURRENT_USER_ERROR,
                undefined,
              );
            }),
            finalize(() => {
              this.commonUtils.updateLoadingModal(false);
            }),
          )
          .subscribe();
      }
    });
  }

  handleGroupChange(code: string): void {
    this.commonUtils.updateBanner(false);
    if (code !== '' && code !== this.currentLocation?.groupCode) {
      this.locationService
        .getLinkedSitesByGroup(code)
        .pipe(
          take(1),
          switchMap((data) => {
            this.linkedSites = data?.response?.linkedSitesPerGroup || [];
            this.linkedSites.sort((a, b) => a.code.localeCompare(b.code));
            if (this.linkedSites.length > 0) {
              return this.locationService
                .getLocationProfile(this.linkedSites[0].code)
                .pipe(takeUntilDestroyed(this.destroy));
            } else {
              this.commonUtils.updateBannerType(BANNER_TYPE.NO_LOCATIONS);
              return of(false);
            }
          }),
          filter((value) => !!value),
        )
        .subscribe((returnValue) => {
          this.locationService.setCurrentLocation$({
            groupCode: code,
            location: (returnValue as GetLocationProfile).response,
          });
        });
    }
  }

  handleLocationChange(code: string): void {
    this.commonUtils.updateBanner(false);
    if (
      code !== '' &&
      code !== this.currentLocation?.location?.locationDetails?.code
    ) {
      this.locationService
        .getLocationProfile(code)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe((returnValue) => {
          this.locationService.setCurrentLocation$({
            groupCode: this.currentLocation?.groupCode ?? '',
            location: returnValue.response,
          });
        });
    }
  }

  openLocation() {
    //Change this when running zoneless
    setTimeout(() => this.locationSearchRef()?.setFocus(), 100);
  }

  openLocationGroup() {
    //Change this when running zoneless
    setTimeout(() => this.groupSearchRef()?.setFocus(), 100);
  }

  get canEditProfile() {
    const role = this.currentUser?.roleName;
    return this.userProfileUtils.canSeeOwnProfile(role);
  }

  private getLocationProfile(user: UserAdmin) {
    this.locationService
      .getLocationProfile(null)
      .pipe(take(1))
      .subscribe((getProfile) => {
        this.currentLocation = {
          groupCode: this.currentUser?.locationGroupCode || '',
          location: getProfile?.response,
        };
        this.locationService.setCurrentLocation$(this.currentLocation);
        this.getLinkedSitesByGroup(user);
      });
  }

  private getLinkedSitesByGroup(user: UserAdmin) {
    if (this.userProfileUtils.canEditUserLocation(user.roleName)) {
      this.locationService
        .getLinkedSitesByGroup(user.locationGroupCode || '')
        .pipe(take(1))
        .subscribe((getSites) => {
          this.linkedSites = this.commonUtils.sortLinkedSite(
            getSites?.response?.linkedSitesPerGroup,
          ) as LinkedSite[];
        });
    } else {
      this.linkedSites = [];
    }
  }

  private getLocationGroups() {
    return this.locationService.getLocationGroups().pipe(
      take(1),
      tap((getGroups) => {
        const sortedGroups = this.commonUtils.sortLinkedSite(
          getGroups?.response,
        ) as LocationGroup[];
        this.locationGroups = sortedGroups;
        this.locationService.setLocationGroups$(sortedGroups);
      }),
    );
  }

  private updateCurrentUser(newLocation: CurrentLocation) {
    this.toggleLoading(true);
    // persist site change to user
    const user = { ...(this.currentUser as UserAdmin) };
    user.locationCode =
      newLocation?.location?.locationDetails?.code ?? user.locationCode;
    user.locationGroupCode = newLocation.groupCode;
    user.locationId =
      newLocation?.location?.locationDetails?.id ?? user.locationId;
    if (this.locationGroups) {
      user.locationGroupId =
        this.locationGroups.find(
          (group) => group.code === newLocation.groupCode,
        )?.id ?? user.locationGroupId;
    }
    this.userService
      .editUser(user)
      .pipe(take(1))
      .subscribe(() => {
        this.toggleLoading(false);
        // TODO work with back-end to return copy of updated user here
        // instead of recycling front-end copy of changes without knowing
        // that db was successful.
        this.currentUser = user;
        this.currentLocation = {
          groupCode: user.locationGroupCode,
          location: newLocation.location,
        };
        this.userService.setCurrentUser$(user);
      });
  }

  private setUserInfo(user: UserAdmin) {
    if (user?.roleName && user?.locationGroupCode && user?.locationCode) {
      this.currentUser = user;
      this.userRole = this.currentUser?.roleName || '';
      this.sciIndicator = user.sciIndicator ?? 0;
      this.userDisplayName.set(
        this.currentUser.familyName.concat(', ', this.currentUser.givenName),
      );
    }

    if (
      user.locationCode !==
        this.currentLocation?.location?.locationDetails?.code ||
      user.locationGroupCode !== this.currentLocation?.groupCode
    ) {
      this.getLocationProfile(user);
    }
  }
}
